<template>
  <!-- User Info -->
  <v-row>
    <v-col cols="12" md="6">
      <v-card flat border class="pa-4 text-start rounded-sm" height="120">
        <strong class="text-subtitle-1 font-weight-bold">{{
          viewModel.hideWords(viewModel.userName.value)
        }}</strong>
        <p class="text-subtitle-2 text-gray">
          {{ viewModel.hideEmailCharacters(viewModel.userEmail.value) }}
        </p>
      </v-card>
    </v-col>
    <!-- Assessor Section -->

    <v-col cols="12" md="6">
      <v-card
        flat
        border
        class="pa-4 text-start rounded-sm overflow-y-auto"
        height="120"
      >
        <v-list-item class="rounded">
          <!-- Avatar del Asesor -->
          <template #prepend>
            <v-avatar
              size="70"
              rounded="lg"
              color="secondary"
              class="text-white"
            >
              <v-icon>mdi-account-tie</v-icon>
            </v-avatar>
          </template>

          <!-- Información del Asesor -->
          <template #default>
            <v-list-item-title class="text-subtitle-1 font-weight-bold">
              {{
                viewModel.userHasAssessor.value
                  ? `Tu Asesor: ${viewModel.assessorName.value}`
                  : "No tienes asesor asignado"
              }}
            </v-list-item-title>
            <v-list-item-subtitle class="text-subtitle-2">
              {{
                viewModel.userHasAssessor.value
                  ? `Contacto: ${
                      viewModel.assessorPhone.value || "No disponible"
                    }`
                  : "Por favor contacta a la cooperativa para más información"
              }}
            </v-list-item-subtitle>
          </template>

          <!-- Botón de Contacto -->
          <template #append>
            <v-btn
              v-if="
                viewModel.userHasAssessor.value && viewModel.assessorPhone.value
              "
              color="secondary"
              variant="text"
              @click="viewModel.callAssessor(viewModel.assessorPhone.value)"
            >
              <v-icon left>mdi-phone</v-icon>
              Llamar
            </v-btn>
          </template>
        </v-list-item>
      </v-card>
    </v-col>
  </v-row>

  <!-- Contributions, Credits, and Savings -->
  <v-row>
    <!-- Tus Aportes -->
    <v-col cols="12" md="4">
      <v-card flat border class="pa-4 text-start">
        <v-card-title class="text-subtitle-1 font-weight-bold px-0"
          >Tus Aportes</v-card-title
        >
      </v-card>
      <v-card
        flat
        border
        class="pa-4 text-start rounded-sm overflow-y-auto"
        height="200"
      >
        <v-card-subtitle v-if="viewModel.loading.value">
          <v-skeleton-loader
            class="mx-4 my-2"
            type="list-item-two-line"
            v-for="n in 1"
            :key="'skeleton-contribution-' + n"
          ></v-skeleton-loader>
        </v-card-subtitle>
        <v-list v-else-if="viewModel.contributions.value.length > 0">
          <v-list-item
            v-for="(contribution, index) in viewModel.contributions.value"
            :key="'contribution-' + index"
            @click="viewModel.goToContributions(contribution)"
            class="pa-4 mb-2 cursor-pointer"
            border
            style="border-left: 7px solid rgb(var(--v-theme-primary), 0.4)"
          >
            <template #default>
              <v-list-item-title class="text-subtitle-1 font-weight-bold"
                >Total</v-list-item-title
              >
              <v-list-item-subtitle class="text-subtitle-1 py-2">
                {{ viewModel.formatCoin(contribution.moneda) }}
                {{ viewModel.formatFloat(contribution.saldoaporte) }}
              </v-list-item-subtitle>
            </template>
            <template #append>
              <v-icon>mdi-chevron-right</v-icon>
            </template>
          </v-list-item>
        </v-list>

        <v-alert
          v-else
          type="info"
          class="text-center text-subtitle-2"
          variant="tonal"
          text="Aún no ha realizado ningún aporte."
          border
        >
        </v-alert>
      </v-card>
    </v-col>

    <!-- Tus Créditos -->
    <v-col cols="12" md="4">
      <v-card flat border class="pa-4 text-start">
        <v-card-title class="text-subtitle-1 font-weight-bold px-0"
          >Tus Créditos</v-card-title
        >
      </v-card>
      <v-card
        class="pa-4 text-start rounded-sm overflow-y-auto"
        height="200"
        border
        flat
      >
        <v-card-subtitle v-if="viewModel.loading.value">
          <v-skeleton-loader
            class="mx-4 my-2"
            type="list-item-two-line"
            v-for="n in 1"
            :key="'skeleton-credit-' + n"
          ></v-skeleton-loader>
        </v-card-subtitle>
        <v-list v-else-if="viewModel.credits.value.length > 0">
          <v-list-item
            v-for="(credit, index) in viewModel.credits.value"
            :key="'credit-' + index"
            @click="viewModel.goToCreditDetail(credit)"
            class="pa-4 mb-2 cursor-pointer"
            border
            style="border-left: 7px solid rgb(var(--v-theme-primary), 0.4)"
          >
            <template #default>
              <v-list-item-title class="text-subtitle-1 font-weight-bold">{{
                credit.nom
              }}</v-list-item-title>
              <v-list-item-subtitle
                class="text-subtitle-1 py-1 d-flex justify-space-between"
              >
                <span> Créd. desembolsado: </span>
                <strong>
                  {{ viewModel.formatCoin(credit.moneda) }}
                  {{ viewModel.formatFloat(credit.montodesembolsado) }}</strong
                >
              </v-list-item-subtitle>
              <v-progress-linear
                :model-value="
                  viewModel.percentDisbursement(
                    credit.montodesembolsado,
                    credit.saldo
                  )
                "
                color="amber"
                class="mt-2 rounded-lg"
                height="9"
              ></v-progress-linear>
            </template>
            <template #append>
              <v-icon>mdi-chevron-right</v-icon>
            </template>
          </v-list-item>
        </v-list>

        <v-alert
          v-else
          type="info"
          class="text-center text-subtitle-2"
          variant="tonal"
          text="Actualmente no tienes créditos pendientes."
          border
        >
        </v-alert>
      </v-card>
    </v-col>

    <!-- Tus Ahorros -->
    <v-col cols="12" md="4">
      <v-card flat border class="pa-4 text-start">
        <v-card-title class="text-subtitle-1 font-weight-bold px-0"
          >Tus Ahorros</v-card-title
        >
      </v-card>
      <v-card
        class="pa-4 text-start rounded-sm overflow-y-auto"
        height="200"
        border
        flat
      >
        <v-card-subtitle v-if="viewModel.loading.value">
          <v-skeleton-loader
            class="mx-4 my-2"
            type="list-item-two-line"
            v-for="n in 1"
            :key="'skeleton-saver-' + n"
          ></v-skeleton-loader>
        </v-card-subtitle>
        <v-list v-else-if="viewModel.savers.value.length > 0">
          <v-list-item
            v-for="(saver, index) in viewModel.savers.value"
            :key="'saver-' + index"
            class="pa-4 mb-2 cursor-pointer"
            border
            style="border-left: 7px solid rgb(var(--v-theme-primary), 0.4)"
            @click.prevent="viewModel.goToSaving(saver)"
          >
            <template #default>
              <v-list-item-title class="text-subtitle-1 font-weight-bold">{{
                viewModel.capitalize(saver.TIPO_AHORRO)
              }}</v-list-item-title>
              <v-list-item-subtitle
                class="text-subtitle-1 py-1 d-flex justify-space-between"
              >
                <span> Saldo disponible: </span>
                <v-spacer></v-spacer>
                <span>
                  {{ viewModel.formatCoin(saver.moneda) }}
                  {{ viewModel.formatFloat(saver.saldo) }}
                </span>
              </v-list-item-subtitle>
            </template>
          </v-list-item>
        </v-list>
        <v-alert
          v-else
          type="info"
          class="text-center text-subtitle-2"
          variant="tonal"
          text="Actualmente no tienes ahorros aperturados."
          border
        >
        </v-alert>
      </v-card>
    </v-col>

    <!-- last transactions-->
    <!-- Últimos Movimientos -->
    <v-col cols="12">
      <v-card
        flat
        border
        class="pa-4 text-start rounded-sm overflow-y-auto"
        height="540"
      >
        <v-card-title class="text-subtitle-1 font-weight-bold">
          Últimos Movimientos
        </v-card-title>
        <!-- Loader Skeleton -->
        <v-card-subtitle v-if="viewModel.loading.value">
          <v-skeleton-loader
            class="mx-4 my-2"
            type="list-item-two-line"
            v-for="n in 3"
            :key="'skeleton-movement-' + n"
          ></v-skeleton-loader>
        </v-card-subtitle>

        <!-- Transactions List -->
        <v-list
          class="bg-transparent"
          v-else-if="viewModel.transactions.value.length > 0"
        >
          <v-list-item
            v-for="(movement, index) in viewModel.transactions.value"
            :key="'movement-' + index"
            class="pa-4 mb-2 border-b-sm"
            style="border-left: 7px solid rgb(var(--v-theme-primary), 0.4)"
          >
            <template #prepend>
              <v-avatar
                rounded="lg"
                size="48"
                variant="tonal"
                :color="viewModel.getColor(movement.tipomov)"
              >
                <v-icon size="20">{{
                  viewModel.getIconName(movement.tipomov)
                }}</v-icon>
              </v-avatar>
            </template>
            <template #default>
              <v-list-item-title class="text-subtitle-1 font-weight-bold">
                {{ movement.concepto }}
              </v-list-item-title>
              <v-list-item-subtitle class="text-subtitle-2 py-1">
                {{ movement.fecha }} -
                {{ movement.hora }}
              </v-list-item-subtitle>
            </template>

            <!-- Append Icon -->
            <template #append>
              <v-list-item-subtitle
                class="text-subtitle-1 font-weight-bold"
                :class="`text-${viewModel.getColor(movement.tipomov)}`"
              >
                <strong>
                  {{ viewModel.formatCoin(movement.moneda) }}
                  {{ viewModel.formatFloat(movement.saldoaporte) }}
                </strong>
              </v-list-item-subtitle>
            </template>
          </v-list-item>
        </v-list>

        <!-- No Transactions Alert -->
        <v-alert
          v-else
          type="info"
          class="text-center text-subtitle-2"
          variant="tonal"
          text="Actualmente no tienes movimientos recientes."
          border
        >
        </v-alert>
      </v-card>
    </v-col>
  </v-row>
</template>

<script lang="ts" setup>
import HomeViewModel from "@/viewmodels/app/HomeViewModel";

const viewModel = new HomeViewModel();
</script>
